/* eslint-disable react-refresh/only-export-components */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../logging/clientLogger';
import { streamPalettes, defaultStreamPalette, fonts } from '../design/palettes';

const c = streamPalettes[defaultStreamPalette];

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

// App-level safety net. Catches render-time errors anywhere below it, ships them
// to CloudWatch via the client error sink, and shows a calm recovery screen
// instead of a blank white page.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError(error.stack ?? error.message, {
      type: 'react',
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />;
    }
    return this.props.children;
  }
}

function DefaultFallback() {
  return (
    <div
      role="alert"
      style={{
        background: c.bg, color: c.ink, minHeight: '100vh',
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <div style={{ fontFamily: fonts.brand, fontSize: 30, fontWeight: 400, letterSpacing: -0.9 }}>
          Something <em style={{ color: c.accent, fontStyle: 'italic' }}>spilled.</em>
        </div>
        <p style={{ fontSize: 14, color: c.mute, marginTop: 10, lineHeight: 1.5 }}>
          Something went wrong on our end. Reloading usually clears it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 18, background: c.ink, color: c.surface, border: 0,
            padding: '12px 18px', borderRadius: 12, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}
